import React from "react";
import { useSelector } from "react-redux";
import Tooltip from '@mui/material/Tooltip';
import Fade from '@mui/material/Fade';
import { Localize } from '../../app/lang/langs';

/**
 * Hover explainer for a permission row: what the scope actually controls.
 *
 * The description comes from the `PERM_DESC_<scope>` lang keys; a scope with no
 * key falls back to rendering the child untouched rather than an empty bubble,
 * so adding a new scope never ships a broken tooltip.
 *
 * Props:
 * - scope: string — scope key (e.g. "collabs")
 * - scopeType: "global" | "team" — drives the footer line
 * - children: the element the tooltip is anchored to (must accept a ref)
 */
export default function PermissionScopeTooltip({ scope, scopeType, children }) {

    // Read the language from the store rather than Localize's localStorage
    // default, so switching language re-renders the description.
    const lang = useSelector((state) => state.settings.lang);

    const descriptionKey = `PERM_DESC_${scope}`;
    const description = Localize(descriptionKey, lang);
    // Localize echoes the key back when it has no translation.
    if (!description || description === descriptionKey) return children;

    const footer = Localize(
        scopeType === "global" ? "PERM_SCOPE_GLOBAL" : "PERM_SCOPE_TEAM",
        lang
    );

    const title = (
        <div style={{ padding: '2px 0' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    color: '#fff',
                    marginBottom: 5,
                }}
            >
                <span
                    style={{
                        display: 'inline-block',
                        width: 3,
                        height: 12,
                        borderRadius: 2,
                        background: 'linear-gradient(180deg, #FF9015, #FFC100)',
                        flexShrink: 0,
                    }}
                />
                {Localize(scope, lang)}
            </div>
            <div
                style={{
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    lineHeight: 1.5,
                    color: 'rgba(255, 255, 255, 0.92)',
                }}
            >
                {description}
            </div>
            <div
                style={{
                    marginTop: 7,
                    paddingTop: 6,
                    borderTop: '1px solid rgba(255, 255, 255, 0.18)',
                    fontSize: '0.68rem',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                    color: 'rgba(255, 205, 70, 0.95)',
                }}
            >
                {footer}
            </div>
        </div>
    );

    return (
        <Tooltip
            title={title}
            arrow
            placement="top-start"
            enterDelay={320}
            enterNextDelay={120}
            leaveDelay={40}
            slots={{ transition: Fade }}
            slotProps={{
                transition: { timeout: { enter: 200, exit: 130 } },
                popper: {
                    modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
                },
                tooltip: {
                    sx: {
                        maxWidth: 290,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: '10px',
                        // Translucent navy glass rather than a solid block, so the
                        // bubble sits lightly on the grid it explains.
                        bgcolor: 'rgba(0, 40, 128, 0.78)',
                        backdropFilter: 'blur(16px) saturate(140%)',
                        WebkitBackdropFilter: 'blur(16px) saturate(140%)',
                        border: '1px solid rgba(255, 255, 255, 0.16)',
                        boxShadow: '0 2px 10px rgba(0, 40, 128, 0.18), 0 8px 24px rgba(0, 40, 128, 0.16)',
                    },
                },
                arrow: {
                    sx: { color: 'rgba(0, 40, 128, 0.78)' },
                },
            }}
        >
            {children}
        </Tooltip>
    );
}
